import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { api, type AdminMessagesPageParams } from "../../../services/api";
import { getUser } from "../../../services/utils";
import { ShowToast } from "../../../services/toast";
import { useUser } from "../../auth/context/user-context";
import { useAdminUsers } from "../../manageAdmin/context/AdminUsersContext";
import { AdminPermission } from "../../manageAdmin/AdminUserList.types";
import type { MailboxSendMessageInput } from "../utils/staged-attachment-utils";

export const MailBoxContext = React.createContext({});

export const CONTACT_FETCH_INTERVAL = 1000 * 10; // 10 seconds
export const CHAT_FETCH_INTERVAL = 1000 * 10; // 10 seconds
const ACTIVE_STATUS_FETCH_INTERVAL = 1000 * 60 * 1; // 1 minutes
const CHAT_PAGE_SIZE = 100;

type FetchChatMessagesMode = "initial" | "older" | "newer";

const mergeMessages = (existingMessages: any[], incomingMessages: any[], position: "prepend" | "append") => {
  if (incomingMessages.length === 0) {
    return existingMessages;
  }

  const existingIds = new Set(existingMessages.map((message) => message.id));
  const uniqueIncomingMessages = incomingMessages.filter((message) => !existingIds.has(message.id));

  if (uniqueIncomingMessages.length === 0) {
    return existingMessages;
  }

  return position === "prepend"
    ? [...uniqueIncomingMessages, ...existingMessages]
    : [...existingMessages, ...uniqueIncomingMessages];
};

export function MailBoxProvider({ children }) {
  const { myPermissions, initialPermissionsLoaded } = useAdminUsers();
  const user = getUser();
  const { userInfo } = useUser() as any;
  const isProvider = userInfo?.type === "P";

  const chatContactsRef = React.useRef<any>(null);
  const chatRef = React.useRef<any>(null);
  const selectedContactRef = React.useRef<any>(null);
  const inquiryRef = React.useRef<any>(null);
  const chatMessagesRef = React.useRef<any[]>([]);
  const conversationKeyRef = React.useRef<string | null>(null);

  const [activeTab, setActiveTab] = useState<"clients" | "providers" | "rbt" | "client-acq" | "ins-ops" | "tech" | "clinical" | "currentClients">("providers");

  // loading states
  const [contactListLoading, setContactListLoading] =
    React.useState<boolean>(false);
  const [inquiryLoading, setInquiryLoading] = React.useState<boolean>(false);
  const [messagesLoading, setMessagesLoading] = React.useState<boolean>(false);
  const [removingSchedule, setRemovingSchedule] =
    React.useState<boolean>(false);
  const [inquiryDeclineLoading, setInquiryDeclineLoading] =
    React.useState<boolean>(false);
  const [inquiryAcceptLoading, setInquiryAcceptLoading] =
    React.useState<boolean>(false);

  const [contactList, setContactList] = React.useState<any>([]);
  const [inquiries, setInquiries] = React.useState<any>([]);
  const [secondaryInsuranceDetails, setSecondaryInsuranceDetails] =
    React.useState<any>({});
  const [insuranceDetails, setInsuranceDetails] = React.useState<any>({});
  const [selectedContact, setSelectedContact] = React.useState<any>(null);
  const [selectedContactStatus, setSelectedContactStatus] =
    React.useState<any>(null);
  const [chatMessages, setChatMessages] = React.useState<any>([]);
  const [hasOlderMessages, setHasOlderMessages] = React.useState<boolean>(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = React.useState<boolean>(false);
  const [messageReadStatus, setMessageReadStatus] = React.useState<any>({});
  const [checkboxLoadingStates, setCheckboxLoadingStates] = React.useState<any>({});

  const isClientTabSelected = activeTab === "clients";

  // A layout effect, so the ref is current before any child's effects run:
  // ChatBox asks for older messages from its own effect in the same commit
  // that delivers the first page, and the older request starts from the ref.
  useLayoutEffect(() => {
    chatMessagesRef.current = chatMessages;
  }, [chatMessages]);

  useEffect(() => {
    const contactId = selectedContact?.id;
    if (!contactId || !user?.id) {
      conversationKeyRef.current = null;
      return;
    }

    conversationKeyRef.current = `${activeTab}:${contactId}:${user.id}`;
  }, [activeTab, selectedContact?.id, user?.id]);


  useEffect(() => {
    if (!myPermissions?.includes(AdminPermission.READ_CHAT_GENERAL_ADMIN) && activeTab === "providers") {
      if (myPermissions?.includes(AdminPermission.READ_CHAT_RBT_ADMIN)) {
        setActiveTab("rbt");
      } else if (myPermissions?.includes(AdminPermission.READ_CHAT_CLIENT_ACQ_ADMIN)) {
        setActiveTab("client-acq");
      } else if (myPermissions?.includes(AdminPermission.READ_CHAT_INS_OPS_ADMIN)) {
        setActiveTab("ins-ops");
      } else if (myPermissions?.includes(AdminPermission.READ_CHAT_TECH_ADMIN)) {
        setActiveTab("tech");
      } else if (myPermissions?.includes(AdminPermission.READ_CHAT_CLINICAL_ADMIN)) {
        setActiveTab("clinical");
      } else if (myPermissions?.includes(AdminPermission.READ_CHAT_CLIENT)) {
        setActiveTab("clients");
      }
    }
  }, [myPermissions, activeTab]);



  const onInquiryDecline = async (id: string) => {
    try {
      setInquiryDeclineLoading(true);
      const response = await api.updateScheduleStatus(id, {
        providerStatus: -1,
      });
      // fetch enquiries again
      if ([200, 201].includes(response.status)) {
        setSelectedContact(null);
        ShowToast({
          message: "Inquiry Declined!",
          type: "success",
        });
        const list = await fetchInquiries();
        if (list.length > 0 && list[0]) {
          setSelectedContact(list[0]);
        }
        setInquiryDeclineLoading(false);
      } else {
        setInquiryDeclineLoading(false);
        ShowToast({
          message: "Unable to decline, please try again later",
          type: "error",
        });
      }
    } catch (error: any) {
      setInquiryDeclineLoading(false);
      ShowToast({
        message: "Something went wrong while declining inquiry",
        type: "error",
      });
    }
  };

  const onInquiryAccept = async (id: string) => {
    try {
      setInquiryAcceptLoading(true);
      const response = await api.updateScheduleStatus(id, {
        providerStatus: 1,
      });
      // fetch enquiries again
      // needs to move in current clients tab
      // fetch contact list again
      // make selected contact as selected
      // fetch chat messages
      // fetch insurance details
      // fetch selected contact status
      if ([200, 201].includes(response.status)) {
        ShowToast({
          message: "Inquiry Accepted!",
          type: "success",
        });
        fetchInquiries();
        const list = await fetchContactList();
        if (list.length > 0 && list.find((e) => e.id === id)) {
          const contact = contactList.find((e) => e.id === id);
          setSelectedContact(contact);
        }
        setInquiryAcceptLoading(false);
        setActiveTab("currentClients");
      } else {
        setInquiryAcceptLoading(false);
      }
    } catch (error: any) {
      setInquiryAcceptLoading(false);
      ShowToast({
        message: "Something went wrong while declining inquiry",
        type: "error",
      });
    }
  };

  const fetchInsuranceDetails = async () => {
    try {
      const insuranceResponse = await api.getInsurance(selectedContact?.id);
      if ([200, 201].includes(insuranceResponse.status)) {
        setInsuranceDetails(
          (insuranceResponse?.data || []).find(
            (e) => e.isActive && e.isPrimary
          ) || {}
        );
        setSecondaryInsuranceDetails(
          (insuranceResponse?.data || []).find(
            (e) => e.isActive && !e.isPrimary
          ) || {}
        );
      }
    } catch (error: any) {
      ShowToast({
        message: "Something went wrong while fetching insurance details",
        type: "error",
      });
    }
  };

  const fetchInquiries = async (isInquiryLoading = false) => {
    try {
      if (isInquiryLoading) {
        setInquiryLoading(true);
      }
      const response = await api.getProviderClients(user?.id, {
        page: 0,
        limit: 1000,
        type: 0,
      });
      setInquiryLoading(false);
      if ([200, 201].includes(response.status)) {
        setInquiries(response?.data?.items || []);
        return response?.data?.items || [];
      }
    } catch (error: any) {
      setInquiryLoading(false);
      ShowToast({
        message: "Something went wrong while fetching inquiries",
        type: "error",
      });
      return [];
    }
    return [];
  };

  const fetchSelectedContactStatus = async () => {
    if (selectedContact) {
      try {
        const id = selectedContact?.id;
        const response = await api.fetchUserStatus(id);
        if (response && [200, 201].includes(response.status)) {
          // set selected contact profile
          setSelectedContactStatus((prev) => ({
            ...prev,
            ...response.data,
          }));
        }
      } catch {
        // Preserve the last presence state until the next background refresh succeeds.
      }
    }
  };

  const removeSchedule = async (id: string) => {
    try {
      setRemovingSchedule(true);
      const response = await api.removeSchedules(id);
      if ([200, 201].includes(response.status)) {
        // fetch enquiries again
        // fetch contact list again
        fetchContactList();
        // also set selected contact to null
        setSelectedContact(null);
        setRemovingSchedule(false);
      } else {
        ShowToast({
          message: "Unable to remove, please try again later",
          type: "error",
        });
      }
    } catch (error: any) {
      setRemovingSchedule(false);
      ShowToast({
        message:
          error?.response?.data?.message ||
          "Something went wrong while removing contact",
        type: "error",
      });
    }
  };

  const fetchContactList = async (isContactLoading = false) => {
    if (!initialPermissionsLoaded) return;

    if (user?.id) {
      try {
        if (isContactLoading) {
          setContactListLoading(true);
        }
        if (activeTab === "clients") {
          if (myPermissions?.includes(AdminPermission.READ_CHAT_CLIENT)) {
            const response = await api.getClientsForAdminChat({
              page: 0,
              limit: 1000,
              type: 1,
            });
            isContactLoading && setContactListLoading(false);
            if ([200, 201].includes(response.status)) {
              setContactList(response?.data?.items || []);
              return response?.data?.items || [];
            }
          }
        } else if (activeTab === "rbt") {
          if (myPermissions?.includes(AdminPermission.READ_CHAT_RBT_ADMIN)) {
            const response = await api.getAdminRbtChatProviders(user?.id);
            isContactLoading && setContactListLoading(false);
            if ([200, 201].includes(response.status)) {
              setContactList(response?.data?.items || []);
              return response?.data?.items || [];
            }
          }
        } else if (activeTab === "client-acq") {
          if (myPermissions?.includes(AdminPermission.READ_CHAT_CLIENT_ACQ_ADMIN)) {
            const response = await api.getAdminClientAcqChatProviders(user?.id);
            isContactLoading && setContactListLoading(false);
            if ([200, 201].includes(response.status)) {
              setContactList(response?.data?.items || []);
              return response?.data?.items || [];
            }
          }
        } else if (activeTab === "ins-ops") {
          if (myPermissions?.includes(AdminPermission.READ_CHAT_INS_OPS_ADMIN)) {
            const response = await api.getAdminInsOpsChatProviders(user?.id);
            isContactLoading && setContactListLoading(false);
            if ([200, 201].includes(response.status)) {
              setContactList(response?.data?.items || []);
              return response?.data?.items || [];
            }
          }
        } else if (activeTab === "tech") {
          if (myPermissions?.includes(AdminPermission.READ_CHAT_TECH_ADMIN)) {
            const response = await api.getAdminTechChatProviders(user?.id);
            isContactLoading && setContactListLoading(false);
            if ([200, 201].includes(response.status)) {
              setContactList(response?.data?.items || []);
              return response?.data?.items || [];
            }
          }
        } else if (activeTab === "clinical") {
          if (myPermissions?.includes(AdminPermission.READ_CHAT_CLINICAL_ADMIN)) {
            const response = await api.getAdminClinicalChatProviders(user?.id);
            isContactLoading && setContactListLoading(false);
            if ([200, 201].includes(response.status)) {
              setContactList(response?.data?.items || []);
              return response?.data?.items || [];
            }
          }
        } else {
          if (myPermissions?.includes(AdminPermission.READ_CHAT_GENERAL_ADMIN)) {
            const response = await api.getFamilyProviders(user?.id);
            isContactLoading && setContactListLoading(false);
            if ([200, 201].includes(response.status)) {
              setContactList(response?.data?.items || []);
              return response?.data?.items || [];
            }
          }
        }
      } catch (error: any) {
        ShowToast({
          message: "Something went wrong while fetching contact list",
          type: "error",
        });
      }
    }
    return [];
  };

  useEffect(() => {
    if (!initialPermissionsLoaded) return;
    fetchContactList();
  }, [initialPermissionsLoaded]);

  const applyMessageUpdate = useCallback((updatedMessage: any) => {
    setChatMessages((prevMessages) =>
      prevMessages.map((message) =>
        message.id === updatedMessage.id ? { ...message, ...updatedMessage } : message
      )
    );
  }, []);

  // Acknowledgment toggle function for manual checkbox feature (issue #22)
  const toggleMessageRead = useCallback(async (messageId: string, shouldAcknowledge: boolean) => {
    // Set checkbox loading state
    setCheckboxLoadingStates(prev => ({
      ...prev,
      [messageId]: true
    }));

    

    try {
      // Single acknowledge API call that returns the complete updated message
      const acknowledgeResponse = await api.acknowledgeMessage(messageId, shouldAcknowledge);

      const acknowledgeData = acknowledgeResponse.data.data;
        
      if ([200, 201].includes(acknowledgeResponse.status)) {
        // Update the specific message with acknowledgment and assignment data from API response
        setChatMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  isRead: acknowledgeData.isRead,
                  acknowledgedBy: acknowledgeData.acknowledgedBy,
                  acknowledgedAt: acknowledgeData.acknowledgedAt,
                  acknowledgedByUser: acknowledgeData.acknowledgedByUser,
                  assignedToUserId: acknowledgeData.assignedToUserId || null,
                  assignedToUser: acknowledgeData.assignedToUser || null,
                  assignedByUserId: acknowledgeData.assignedByUserId || null,
                  assignedByUser: acknowledgeData.assignedByUser || null,
                  assignedAt: acknowledgeData.assignedAt || null,
                }
              : msg
          )
        );


        // Update local acknowledgment state
        setMessageReadStatus((prev) => ({
          ...prev,
          [messageId]: shouldAcknowledge,
        }));

        // Clear checkbox loading state after all API calls complete
        setCheckboxLoadingStates((prev) => ({
          ...prev,
          [messageId]: false,
        }));
      }
    } catch (error) {
      // Revert optimistic update on error - restore original state completely
      setChatMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === messageId ? { 
            ...msg, 
            isRead: !shouldAcknowledge,
            // Restore acknowledgment fields to original state if we were unacknowledging
            ...(!shouldAcknowledge ? {
              acknowledgedBy: msg.acknowledgedBy,
              acknowledgedAt: msg.acknowledgedAt, 
              acknowledgedByUser: msg.acknowledgedByUser
            } : {})
          } : msg
        )
      );
      ShowToast({
        message: "Error updating acknowledgment status",
        type: "error",
      });
      console.error("Failed to update message acknowledgment status:", error);
      
      // Clear checkbox loading state on error
      setCheckboxLoadingStates(prev => ({
        ...prev,
        [messageId]: false
      }));
    }
  }, [selectedContact, user, activeTab]);

  const fetchChatMessages = useCallback(
    async ({
      mode = "newer",
      isMessagesLoading = false,
    }: {
      mode?: FetchChatMessagesMode;
      isMessagesLoading?: boolean;
    } = {}) => {
      if (!selectedContact || !user?.id) {
        return [];
      }

      const providerId = isClientTabSelected ? user.id : selectedContact.id;
      const patientId = isClientTabSelected ? selectedContact.id : user.id;
      const requestConversationKey = `${activeTab}:${selectedContact.id}:${user.id}`;
      const pagination: AdminMessagesPageParams = {
        limit: CHAT_PAGE_SIZE,
      };
      const currentMessages = chatMessagesRef.current;
      let normalizedMode = mode;

      if (normalizedMode === "older") {
        const oldestMessage = currentMessages[0];
        if (!oldestMessage) {
          return [];
        }

        pagination.beforeCreatedAt = oldestMessage.createdAt;
        pagination.beforeId = oldestMessage.id;
        setLoadingOlderMessages(true);
      }

      if (normalizedMode === "newer") {
        const latestMessage = currentMessages[currentMessages.length - 1];
        if (latestMessage) {
          pagination.afterCreatedAt = latestMessage.createdAt;
          pagination.afterId = latestMessage.id;
        } else {
          normalizedMode = "initial";
        }
      }

      if (isMessagesLoading) {
        setMessagesLoading(true);
      }

      try {
        let response;
        switch (activeTab) {
          case "clients":
            response = await api.getAdminClientChatMessages(patientId, pagination);
            break;
          case "providers":
            response = await api.getAdminGeneralChatMessages(providerId, pagination);
            break;
          case "rbt":
            response = await api.getAdminRbtChatMessages(providerId, pagination);
            break;
          case "client-acq":
            response = await api.getAdminClientAcqChatMessages(providerId, pagination);
            break;
          case "ins-ops":
            response = await api.getAdminInsOpsMessages(providerId, pagination);
            break;
          case "tech":
            response = await api.getAdminTechMessages(providerId, pagination);
            break;
          case "clinical":
            response = await api.getAdminClinicalMessages(providerId, pagination);
            break;
          default:
            response = await api.getMessages(patientId, providerId);
            break;
        }

        if (![200, 201].includes(response.status) || requestConversationKey !== conversationKeyRef.current) {
          return [];
        }

        const data = Array.isArray(response.data)
          ? { items: response.data, hasMoreOlder: false, hasMoreNewer: false }
          : response.data;

        if (normalizedMode === "older") {
          setChatMessages((prevMessages) => mergeMessages(prevMessages, data.items || [], "prepend"));
        } else if (normalizedMode === "newer" && pagination.afterCreatedAt) {
          setChatMessages((prevMessages) => mergeMessages(prevMessages, data.items || [], "append"));
        } else {
          setChatMessages(data.items || []);
        }

        if (normalizedMode !== "newer") {
          setHasOlderMessages(Boolean(data.hasMoreOlder));
        }

        return data.items || [];
      } catch (error: any) {
        if (normalizedMode !== "newer") {
          ShowToast({
            message: "Something went wrong while fetching chat messages",
            type: "error",
          });
        }
        return [];
      } finally {
        if (isMessagesLoading) {
          setMessagesLoading(false);
        }
        if (normalizedMode === "older") {
          setLoadingOlderMessages(false);
        }
      }
    },
    [activeTab, isClientTabSelected, selectedContact, user?.id]
  );

  const sendMessage = async ({
    content,
    contentType,
    replyToMessageId,
    idempotencyKey,
    attachments,
  }: MailboxSendMessageInput): Promise<boolean> => {
    if (selectedContact && user?.id) {
      try {
        const payload = {
          providerId: isClientTabSelected
            ? undefined
            : selectedContact?.id,
          patientId: isClientTabSelected
            ? selectedContact?.id
            : user?.id,
          isPatientSender: !isClientTabSelected,
          content,
          contentType: contentType || "text",
          replyToMessageId,
          ...(idempotencyKey ? { idempotencyKey } : {}),
          ...(attachments?.length ? { attachments } : {}),
        };

        let adminType;
        switch (activeTab) {
          case "rbt":
            adminType = "A-RBT";
            break;
          case "client-acq":
            adminType = "A-CLIENT-ACQ";
            break;
          case "ins-ops":
            adminType = "A-INS-OPS";
            break;
          case "tech":
            adminType = "A-TECH";
            break;
          case "clinical":
            adminType = "A-CLINICAL";
            break;
          case "clients":
          case "providers":
            adminType = "A";
            break;
        }

        const response = await api.sendMessageAsAdmin(payload, adminType);
        const sent = [200, 201].includes(response.status);
        if (sent) {
          setContactList([
            ...contactList.filter((contact) => contact?.id === selectedContact.id),
            ...contactList.filter((contact) => contact?.id !== selectedContact.id),
          ]);
          fetchChatMessages({ mode: "newer" });
        }
        return sent;
      } catch (error) {
        ShowToast({
          message: "Something went wrong while sending message",
          type: "error",
        });
        return false;
      }
    }
    return false;
  };

  const doesUserHaveWritePermissionForCurrentChat = () => {
    let hasWriteAccess = false;
    switch (activeTab) {
      case "providers":
        hasWriteAccess = myPermissions?.includes(AdminPermission.WRITE_CHAT_GENERAL_ADMIN);
        break;
      case "clients":
        hasWriteAccess = myPermissions?.includes(AdminPermission.WRITE_CHAT_CLIENT);
        break;
      case "rbt":
        hasWriteAccess = myPermissions?.includes(AdminPermission.WRITE_CHAT_RBT_ADMIN);
        break;
      case "client-acq":
        hasWriteAccess = myPermissions?.includes(AdminPermission.WRITE_CHAT_CLIENT_ACQ_ADMIN);
        break;
      case "ins-ops":
        hasWriteAccess = myPermissions?.includes(AdminPermission.WRITE_CHAT_INS_OPS_ADMIN);
        break;
      case "tech":
        hasWriteAccess = myPermissions?.includes(AdminPermission.WRITE_CHAT_TECH_ADMIN);
        break;
      case "clinical":
        hasWriteAccess = myPermissions?.includes(AdminPermission.WRITE_CHAT_CLINICAL_ADMIN);
        break;
      default:
        hasWriteAccess = false;
    }
    return hasWriteAccess
  }

  useEffect(() => {
    if (selectedContact && user?.id) {
      setChatMessages([]);
      setHasOlderMessages(false);
      setLoadingOlderMessages(false);
      fetchChatMessages({ mode: "initial", isMessagesLoading: true });

      if (chatRef.current) {
        clearInterval(chatRef.current);
        chatRef.current = null;
      }
      chatRef.current = setInterval(() => {
        fetchChatMessages({ mode: "newer" });
      }, CHAT_FETCH_INTERVAL);

      // DISABLED: Auto-read functionality removed for manual acknowledgment feature (issue #22)
      // Messages will now be manually acknowledged via checkboxes
      // if (doesUserHaveWritePermissionForCurrentChat()) {
      //   let payload = {
      //     tab: activeTab,
      //   };
      //   if (isClientTabSelected) {
      //     payload["patientId"] = selectedContact?.id;
      //   } else {
      //     payload["providerId"] = selectedContact?.id;
      //   }
      //   api.markAsRead(payload).then(async (response) => {
      //     if ([200, 201].includes(response.status)) {
      //       // fetch chat messages again
      //       await fetchUnreadConvCounts();
      //       fetchContactList();
      //     }
      //   });
      // }

      // if user is provider then fetch insurance details of the selected contact
      if (isClientTabSelected) {
        fetchInsuranceDetails();
      }

      // we also need to fetch active status of the selected contact
      // fetch get profile api
      // call immediately for the first time
      // then call for every 5 minutes
      fetchSelectedContactStatus();
      if (selectedContactRef.current) {
        clearInterval(selectedContactRef.current);
        selectedContactRef.current = null;
      }
      selectedContactRef.current = setInterval(() => {
        fetchSelectedContactStatus();
      }, ACTIVE_STATUS_FETCH_INTERVAL);
    }

    return () => {
      clearInterval(chatRef.current);
      clearInterval(selectedContactRef.current);
      chatRef.current = null;
      selectedContactRef.current = null;
    };
  }, [selectedContact, user?.id, isClientTabSelected, activeTab, fetchChatMessages]);

  useEffect(() => {
    if (user?.id) {
      // call for chat list

      // should be called every minute
      // call immediately for the first time
      fetchContactList(true);
      chatContactsRef.current = setInterval(() => {
        fetchContactList();
      }, CONTACT_FETCH_INTERVAL);

      if (isProvider) {
        // fetch enquiries
        fetchInquiries(true);
        inquiryRef.current = setInterval(() => {
          fetchInquiries();
        }, CONTACT_FETCH_INTERVAL);
      }
    }
    return () => {
      clearInterval(chatContactsRef.current);
      chatContactsRef.current = null;
      clearInterval(inquiryRef.current);
      inquiryRef.current = null;
    };
  }, [user?.id, isProvider]);

  // when tab changes, we need to fetch the contact list again
  useEffect(() => {
    if (user?.id) {
      setContactList([]);
      fetchContactList(true);
      clearInterval(chatContactsRef.current);
      chatContactsRef.current = setInterval(() => {
        fetchContactList();
      }, CONTACT_FETCH_INTERVAL);
    }
    return () => {
      clearInterval(chatContactsRef.current);
      chatContactsRef.current = null;
    };
  }, [activeTab]);

  const loadOlderMessages = useCallback(
    () => fetchChatMessages({ mode: "older" }),
    [fetchChatMessages]
  );

  const values = {
    selectedContact,
    setSelectedContact,
    selectedContactStatus,
    contactList,
    contactListLoading,
    sendMessage,
    chatMessages,
    messagesLoading,
    hasOlderMessages,
    loadingOlderMessages,
    removeSchedule,
    removingSchedule,
    inquiries,
    inquiryLoading,
    insuranceDetails,
    secondaryInsuranceDetails,
    onInquiryDecline,
    onInquiryAccept,
    inquiryDeclineLoading,
    inquiryAcceptLoading,
    activeTab,
    setActiveTab,
    doesUserHaveWritePermissionForCurrentChat,
    toggleMessageRead,
    messageReadStatus,
    checkboxLoadingStates,
    loadOlderMessages,
    applyMessageUpdate,
    editMessage: async (messageId: string, newContent: string) => {
      try {
        const response = await api.editMessage(messageId, newContent);
        if ([200, 201].includes(response.status)) {
          applyMessageUpdate({
            id: messageId,
            content: newContent,
            editedAt: response.data.data.editedAt,
          });
          return true;
        }
        return false;
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || 'Failed to edit message';
        ShowToast({ message: errorMessage, type: 'error' });
        return false;
      }
    },
    refetchMessages: () => fetchChatMessages({ mode: "initial", isMessagesLoading: true }),
  };

  return (
    <MailBoxContext.Provider value={values}>{children}</MailBoxContext.Provider>
  );
}

export function useMailBox() {
  const context = React.useContext(MailBoxContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
